/*
  Danis Drip – Bundle-Ersparnis
  Liest für Bundles die Bestandteile live über die Storefront API (tokenloser
  Zugriff, nur öffentliche Produktdaten) und zeigt an, wie viel man gegenüber
  dem Einzelkauf spart. Keine Pflege nötig: neue Bundles und Preisänderungen
  der Einzelprodukte werden automatisch berücksichtigt.
*/
const DD_API = '/api/2026-10/graphql.json';
const DD_BATCH = 20;
const ddCache = new Map();
const ddPending = new Map();
let ddTimer = null;

function ddQuery(withCountry) {
  return (
    'query DDBundleSavings($ids: [ID!]!' +
    (withCountry ? ', $country: CountryCode) @inContext(country: $country)' : ')') +
    ' { nodes(ids: $ids) { ... on ProductVariant { id price { amount currencyCode }' +
    ' components(first: 10) { nodes { quantity productVariant { price { amount } } } } } } }'
  );
}

function ddCents(amount) {
  return Math.round(parseFloat(amount) * 100);
}

function ddCompute(node) {
  if (!node || !node.price || !node.components) return null;
  const parts = node.components.nodes || [];
  if (!parts.length) return null;
  const price = ddCents(node.price.amount);
  let sum = 0;
  for (const part of parts) {
    if (!part.productVariant || !part.productVariant.price) return null;
    sum += ddCents(part.productVariant.price.amount) * (part.quantity || 1);
  }
  const save = sum - price;
  if (!(price > 0) || !(save > 0)) return null;
  return { sum, save, currency: node.price.currencyCode };
}

async function ddFetch(country, entries) {
  try {
    const variables = { ids: entries.map((e) => e.gid) };
    if (country) variables.country = country;
    const res = await fetch(DD_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query: ddQuery(!!country), variables }),
    });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const json = await res.json();
    const nodes = (json && json.data && json.data.nodes) || [];
    const byId = new Map(nodes.filter(Boolean).map((n) => [n.id, n]));
    entries.forEach((e) => e.resolve(ddCompute(byId.get(e.gid))));
  } catch (err) {
    entries.forEach((e) => e.resolve(null));
  }
}

function ddFlush() {
  ddTimer = null;
  const groups = new Map();
  ddPending.forEach((entry) => {
    const list = groups.get(entry.country) || [];
    list.push(entry);
    groups.set(entry.country, list);
  });
  ddPending.clear();
  groups.forEach((list, country) => {
    for (let i = 0; i < list.length; i += DD_BATCH) {
      ddFetch(country, list.slice(i, i + DD_BATCH));
    }
  });
}

function ddRequest(variantId, country) {
  const gid = 'gid://shopify/ProductVariant/' + variantId;
  const key = gid + '|' + (country || '');
  if (ddCache.has(key)) return ddCache.get(key);
  const promise = new Promise((resolve) => {
    ddPending.set(key, { gid, country: country || '', resolve });
  });
  ddCache.set(key, promise);
  if (!ddTimer) ddTimer = setTimeout(ddFlush, 25);
  return promise;
}

function ddMoney(cents, currency, lang) {
  const locale = lang === 'en' ? 'en-IE' : 'de-DE';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  } catch (err) {
    return (cents / 100).toFixed(2) + ' ' + currency;
  }
}

class DDBundleSavings extends HTMLElement {
  connectedCallback() {
    const id = this.dataset.variantId;
    if (!id) {
      this.dataset.state = 'none';
      return;
    }
    const lang = this.dataset.lang === 'en' ? 'en' : 'de';
    ddRequest(id, this.dataset.country).then((result) => {
      if (!this.isConnected) return;
      if (!result) {
        this.dataset.state = 'none';
        return;
      }
      this.ddRender(result, lang);
    });
  }

  ddRender(result, lang) {
    const text =
      lang === 'en'
        ? { regular: 'Bought separately ', save: 'You save ' }
        : { regular: 'Einzeln gekauft ', save: 'Du sparst ' };
    this.textContent = '';
    if (this.dataset.mode === 'full') {
      const regular = document.createElement('span');
      regular.className = 'dd-bundle-savings__regular';
      regular.textContent = text.regular + ddMoney(result.sum, result.currency, lang);
      const dot = document.createElement('span');
      dot.className = 'dd-bundle-savings__dot';
      dot.setAttribute('aria-hidden', 'true');
      dot.textContent = ' · ';
      this.append(regular, dot);
    }
    const save = document.createElement('strong');
    save.className = 'dd-bundle-savings__save';
    save.textContent = text.save + ddMoney(result.save, result.currency, lang);
    this.append(save);
    this.dataset.state = 'ready';
  }
}

if (!customElements.get('dd-bundle-savings')) {
  customElements.define('dd-bundle-savings', DDBundleSavings);
}
